import countries from '../../countries';
import { useOptions } from '../useOptions';
import useTranslation from '../useTranslation';

// The embed is using a locale, for example 'nl', and the user might
// also have a specific locale in their browser. In all those cases,
// we want to put the assumed countries first in the list.
//
// So, for example. I'm using the embed in the "en" locale.
// My browser has the locales: ["en-US", "en", "nl"]
// We want to put "gb", "us", "nl" and "be" first in the list.
const highlightCountriesByLocale = {
  nl: ['nl', 'be'],
  fr: ['fr', 'be'],
  de: ['de', 'at'],
  it: ['it', 'ch'],
  es: ['es'],
  en: ['gb', 'us'],
};

const highlightCountryOptions = (locale: string) => {
  let highlightedCountries = [];

  // Retrieve highlighed countries by the locale of the embed
  if (locale in highlightCountriesByLocale) {
    highlightedCountries.push(
      ...highlightCountriesByLocale[locale as keyof typeof highlightCountriesByLocale],
    );
  }

  // Retrieve highlighed countries by the browser locales
  if (navigator.languages && navigator.languages.length > 0) {
    navigator.languages.forEach((language) => {
      const browserLocale = language.slice(0, 2);

      if (browserLocale in highlightCountriesByLocale) {
        highlightedCountries.push(
          ...highlightCountriesByLocale[browserLocale as keyof typeof highlightCountriesByLocale],
        );
      }
    });
  }

  // Make the array unique and sort it on the country name
  highlightedCountries = [...new Set(highlightedCountries)].sort((a, b) => a.localeCompare(b));

  return highlightedCountries;
};

export function useFlatCountryOptions() {
  return Object.entries(countries).map(([countryKey, countryValue]) => ({
    label: countryValue,
    value: countryKey,
  }));
}

export function useCountryOptions() {
  const options = useOptions();
  const translate = useTranslation();

  const countriesOptions = Object.entries(countries).map(([countryKey, countryValue]) => ({
    label: countryValue,
    value: countryKey,
  }));

  const highlightedCountries = highlightCountryOptions(options.locale);

  const groups = [
    {
      label: translate('form.country_all'),
      options: countriesOptions,
    },
  ];

  if (highlightedCountries.length > 0) {
    groups.unshift({
      label: translate('form.country_highlighted'),
      options: highlightedCountries.map((country) => ({
        label: countries[country as keyof typeof countries],
        value: country,
      })),
    });
  }

  return groups;
}
